/*
 * Lightweight StarField component for better LCP performance
 * Minimal initial render with progressive enhancement
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const LightweightStarField = ({ isPaused = false }) => {
  const [isReady, setIsReady] = useState(false);
  const StarFieldComponent = useRef(null);

  useEffect(() => {
    // Load the full StarField component after critical content
    const loadFullStarField = async () => {
      try {
        const { default: StarField } = await import("./StarField");
        StarFieldComponent.current = StarField;

        // Wait for LCP to complete before showing stars
        if ("requestIdleCallback" in window) {
          requestIdleCallback(() => setIsReady(true), { timeout: 3000 });
        } else {
          setTimeout(() => setIsReady(true), 2000);
        }
      } catch (error) {
        console.warn("Failed to load StarField:", error);
      }
    };

    loadFullStarField();
  }, []);

  // Always show the gradient background
  return (
    <>
      {/* Gradient background layer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          minHeight: "100vh",
          minWidth: "100vw",
          zIndex: -1,
          background: "radial-gradient(220% 105% at top center, #1B2947 10%, #75517D 40%, #E96F92 65%, #F7F7B6)",
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          pointerEvents: "none",
          transform: "translateZ(0)", // Force hardware acceleration
        }}
        aria-hidden="true"
      />

      {/* StarField layer (if loaded) - passes isPaused prop */}
      {isReady && StarFieldComponent.current && React.createElement(StarFieldComponent.current, { isPaused })}
    </>
  );
};

LightweightStarField.propTypes = {
  isPaused: PropTypes.bool,
};

export default LightweightStarField;
